import { useEffect, useMemo, useRef } from 'react';
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type PaginationState,
    type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
} from 'components/ui/table';
import { cn } from 'lib/utils';

import {
    clampReportPage,
    REPORT_PAGE_LIMITS,
    reportLimitToPageSize,
    type ReportPageLimit,
} from './reportQueryParams';
import {
    formatReportDisplayCellValue,
    getReportColumnById,
    getReportColumnFooterAggregate,
    parseReportDateTime,
    type ReportColumnDefinition,
} from './reportTableTypes';

type ReportDataTableProps<TRow extends Record<string, unknown>> = {
    data: TRow[];
    columnDefinitions: ReportColumnDefinition<TRow>[];
    isLoading?: boolean;
    globalFilter: string;
    selectedColumnIds: string[];
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
    page: number;
    limit: ReportPageLimit;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: ReportPageLimit) => void;
};

function parseReportDate(value: string | number | null): number {
    if (typeof value !== 'string') {
        return 0;
    }

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function parseReportPageLimit(value: string): ReportPageLimit {
    if (value === '25') {
        return 25;
    }

    if (value === '50') {
        return 50;
    }

    if (value === '100') {
        return 100;
    }

    if (value === '250') {
        return 250;
    }

    if (value === 'all') {
        return 'all';
    }

    return 25;
}

export function ReportDataTable<TRow extends Record<string, unknown>>({
    data,
    columnDefinitions,
    isLoading = false,
    globalFilter,
    selectedColumnIds,
    sorting,
    onSortingChange,
    page,
    limit,
    onPageChange,
    onLimitChange,
}: ReportDataTableProps<TRow>) {
    const visibleColumnDefs = useMemo(
        () =>
            selectedColumnIds
                .map((columnId) => getReportColumnById(columnDefinitions, columnId))
                .filter((def): def is ReportColumnDefinition<TRow> => def !== undefined),
        [selectedColumnIds, columnDefinitions],
    );
    const activeSortedColumnId = sorting[0]?.id;

    const columns = useMemo<ColumnDef<TRow>[]>(
        () =>
            visibleColumnDefs.map((def) => ({
                id: def.id,
                accessorFn: def.getValue,
                header: def.label,
                enableSorting: def.sortable !== false,
                ...(def.sortType === 'datetime' && {
                    sortingFn: (rowA, rowB, columnId) => {
                        const a = parseReportDateTime(
                            rowA.getValue(columnId) as string | number | null,
                        );
                        const b = parseReportDateTime(
                            rowB.getValue(columnId) as string | number | null,
                        );
                        return a === b ? 0 : a > b ? 1 : -1;
                    },
                }),
                ...(def.sortType === 'date' && {
                    sortingFn: (rowA, rowB, columnId) => {
                        const a = parseReportDate(
                            rowA.getValue(columnId) as string | number | null,
                        );
                        const b = parseReportDate(
                            rowB.getValue(columnId) as string | number | null,
                        );
                        return a === b ? 0 : a > b ? 1 : -1;
                    },
                }),
                cell: ({ getValue }) =>
                    formatReportDisplayCellValue(
                        def as ReportColumnDefinition<unknown>,
                        getValue<string | number | null>(),
                    ),
            })),
        [visibleColumnDefs],
    );

    const filteredRowCount = useMemo(() => {
        const query = globalFilter.trim().toLowerCase();
        if (!query) {
            return data.length;
        }

        return data.filter((row) =>
            visibleColumnDefs.some((def) => {
                const raw = def.getValue(row);
                const formatted = formatReportDisplayCellValue(
                    def as ReportColumnDefinition<unknown>,
                    raw,
                );
                return (
                    String(raw ?? '')
                        .toLowerCase()
                        .includes(query) || String(formatted).toLowerCase().includes(query)
                );
            }),
        ).length;
    }, [data, globalFilter, visibleColumnDefs]);

    const pageSize = reportLimitToPageSize(limit, filteredRowCount);
    const pageIndex = page - 1;
    const pagination = useMemo<PaginationState>(
        () => ({
            pageIndex,
            pageSize,
        }),
        [pageIndex, pageSize],
    );

    const pageRef = useRef(page);
    pageRef.current = page;

    useEffect(() => {
        if (isLoading) {
            return;
        }

        const clampedPage = clampReportPage(pageRef.current, limit, filteredRowCount);
        if (clampedPage !== pageRef.current) {
            onPageChange(clampedPage);
        }
    }, [filteredRowCount, isLoading, limit, onPageChange]);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            globalFilter,
            pagination,
        },
        autoResetPageIndex: false,
        onSortingChange: (updater) => {
            const next = typeof updater === 'function' ? updater(sorting) : updater;
            onSortingChange(next);
        },
        onPaginationChange: (updater) => {
            const next = typeof updater === 'function' ? updater(pagination) : updater;
            if (next.pageIndex !== pageIndex) {
                onPageChange(next.pageIndex + 1);
            }
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        globalFilterFn: (row, _columnId, filterValue) => {
            const query = String(filterValue).toLowerCase();
            if (!query) {
                return true;
            }

            const searchableValues = visibleColumnDefs.flatMap((def) => {
                const raw = def.getValue(row.original);
                const formatted = formatReportDisplayCellValue(
                    def as ReportColumnDefinition<unknown>,
                    raw,
                );
                return [String(raw ?? ''), String(formatted)];
            });

            return searchableValues.some((value) => value.toLowerCase().includes(query));
        },
    });

    const filteredRows = table.getFilteredRowModel().rows;
    const displayRows = table.getRowModel().rows;
    const pageCount = table.getPageCount();
    const pageStart = filteredRowCount === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
    const pageEnd = Math.min((pagination.pageIndex + 1) * pagination.pageSize, filteredRowCount);
    const showPaginationControls = limit !== 'all' && pageCount > 1;

    const footerCells: React.ReactNode[] = [];
    let totalsLabelPlaced = false;
    let nonSummableRun = 0;

    visibleColumnDefs.forEach((def) => {
        if (!def.summable) {
            nonSummableRun += 1;
            return;
        }

        if (!totalsLabelPlaced && nonSummableRun > 0) {
            footerCells.push(
                <TableCell key="totals-label" colSpan={nonSummableRun} className="font-semibold">
                    Totals
                </TableCell>,
            );
            totalsLabelPlaced = true;
        } else if (nonSummableRun > 0) {
            footerCells.push(<TableCell key={`pad-${def.id}`} colSpan={nonSummableRun} />);
        }
        nonSummableRun = 0;

        const aggregate = getReportColumnFooterAggregate(
            def as ReportColumnDefinition<unknown>,
            filteredRows.map((row) => row.original),
        );
        const formatted =
            aggregate == null
                ? '—'
                : formatReportDisplayCellValue(def as ReportColumnDefinition<unknown>, aggregate);

        footerCells.push(
            <TableCell
                key={def.id}
                className={cn(
                    'font-semibold tabular-nums',
                    def.align === 'right' && 'text-right',
                    activeSortedColumnId === def.id && 'reports-sorted-column',
                )}
            >
                <span className="inline-flex items-baseline justify-end gap-1">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        {def.format === 'percent' ? 'avg' : 'sum'}
                    </span>
                    <span>{formatted}</span>
                </span>
            </TableCell>,
        );
        totalsLabelPlaced = true;
    });

    if (nonSummableRun > 0) {
        footerCells.push(
            totalsLabelPlaced ? (
                <TableCell key="pad-end" colSpan={nonSummableRun} />
            ) : (
                <TableCell key="totals-label" colSpan={nonSummableRun} className="font-semibold">
                    Totals
                </TableCell>
            ),
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="reports-table-scroll min-h-0 flex-1 overflow-auto rounded-md border border-border">
                {isLoading ? (
                    <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                        Loading report...
                    </div>
                ) : (
                    <Table className="reports-data-table">
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow
                                    key={headerGroup.id}
                                    className="border-b border-border hover:bg-transparent"
                                >
                                    {headerGroup.headers.map((header) => {
                                        const def = getReportColumnById(
                                            columnDefinitions,
                                            header.column.id,
                                        );
                                        const canSort = header.column.getCanSort();
                                        const sorted = header.column.getIsSorted();

                                        return (
                                            <TableHead
                                                key={header.id}
                                                className={cn(
                                                    'text-foreground',
                                                    def?.align === 'right' && 'text-right',
                                                    activeSortedColumnId === header.column.id &&
                                                        'reports-sorted-column',
                                                )}
                                            >
                                                {header.isPlaceholder ? null : canSort ? (
                                                    <button
                                                        type="button"
                                                        className="reports-sort-button"
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext(),
                                                        )}
                                                        {sorted === 'asc' ? (
                                                            <ArrowUp
                                                                className={cn(
                                                                    'reports-sort-icon reports-sort-icon-active h-3.5 w-3.5 shrink-0',
                                                                )}
                                                            />
                                                        ) : sorted === 'desc' ? (
                                                            <ArrowDown
                                                                className={cn(
                                                                    'reports-sort-icon reports-sort-icon-active h-3.5 w-3.5 shrink-0',
                                                                )}
                                                            />
                                                        ) : (
                                                            <ArrowUpDown className="reports-sort-icon h-3.5 w-3.5 shrink-0" />
                                                        )}
                                                    </button>
                                                ) : (
                                                    <span className="whitespace-nowrap">
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext(),
                                                        )}
                                                    </span>
                                                )}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>

                        <TableBody>
                            {displayRows.length ? (
                                displayRows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell) => {
                                            const def = getReportColumnById(
                                                columnDefinitions,
                                                cell.column.id,
                                            );

                                            return (
                                                <TableCell
                                                    key={cell.id}
                                                    className={cn(
                                                        'whitespace-nowrap',
                                                        def?.align === 'right' &&
                                                            'text-right tabular-nums',
                                                        def?.muted && 'text-muted-foreground',
                                                        activeSortedColumnId === cell.column.id &&
                                                            'reports-sorted-column',
                                                    )}
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext(),
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-center"
                                    >
                                        No results.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>

                        <TableFooter className="bg-transparent">
                            <TableRow
                                className={cn(
                                    'hover:bg-transparent',
                                    filteredRows.length === 0 && 'hidden',
                                )}
                            >
                                {footerCells}
                            </TableRow>
                        </TableFooter>
                    </Table>
                )}
            </div>

            {!isLoading && filteredRowCount > 0 ? (
                <div className="mt-3 flex shrink-0 flex-wrap items-center justify-between gap-3 pb-1 text-sm text-muted-foreground">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                            <Label
                                htmlFor="report-page-limit"
                                className="text-xs font-semibold uppercase tracking-wide"
                            >
                                Rows per page
                            </Label>
                            <Select
                                value={String(limit)}
                                onValueChange={(value) =>
                                    onLimitChange(parseReportPageLimit(value))
                                }
                            >
                                <SelectTrigger
                                    id="report-page-limit"
                                    className="h-8 w-[88px] px-2 py-0 focus:ring-offset-0"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="reports-light-overlay">
                                    {REPORT_PAGE_LIMITS.map((option) => (
                                        <SelectItem key={String(option)} value={String(option)}>
                                            {option === 'all' ? 'All' : option}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <span>
                            Showing {pageStart}–{pageEnd} of {filteredRowCount}
                        </span>
                    </div>
                    {showPaginationControls ? (
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => onPageChange(page - 1)}
                                disabled={page <= 1}
                            >
                                <ChevronLeft className="h-4 w-4" />
                                Previous
                            </Button>
                            <span className="tabular-nums">
                                Page {pagination.pageIndex + 1} of {Math.max(pageCount, 1)}
                            </span>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => onPageChange(page + 1)}
                                disabled={page >= pageCount}
                            >
                                Next
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
